import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, MetricCard, BtnPrimary, BtnSecondary, BtnGhost,
  Modal, Field, Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import SmartInput from "@/components/SmartInput";
import { toast } from "sonner";
import { Plus, DownloadSimple, PencilSimple, Trash, Eye, FolderOpen, ChartLineUp } from "@phosphor-icons/react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";

const BLANK = {
  code: "", name: "", customer: "", customer_code: "",
  description: "", start_date: "", end_date: "",
  budget_usd: 0, status: "active", active: true, notes: "",
};

function fmtUSD(n) {
  if (typeof n === "string") return n;
  return `$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function Projects() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [view, setView] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [viewTab, setViewTab] = useState("cost");
  const canManage = can(user.role, "manager");

  async function load() {
    try { const { data } = await api.get("/projects"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/customers?active_only=true"); setCustomers(data); }
      catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, []);

  function openCreate() { setEditing(null); setForm(BLANK); setEditOpen(true); }
  function openEdit(p) {
    setEditing(p);
    setForm({
      ...BLANK, ...p,
      start_date: p.start_date ? String(p.start_date).slice(0, 10) : "",
      end_date: p.end_date ? String(p.end_date).slice(0, 10) : "",
    });
    setEditOpen(true);
  }
  async function save(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        budget_usd: Number(form.budget_usd || 0),
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      if (editing) await api.put(`/projects/${editing.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Saved"); setEditOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(p) {
    if (!window.confirm(`Delete project ${p.code}?`)) return;
    try { await api.delete(`/projects/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function openView(p) {
    setView(p); setSummary(null); setPnl(null); setViewTab("cost");
    try { const { data } = await api.get(`/projects/${p.id}/summary`); setSummary(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function loadPnl(projectId) {
    setPnl(null);
    try { const { data } = await api.get(`/projects/${projectId}/pnl`); setPnl(data); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="projects-page">
      <PageHeader
        overline="Module / Cost Centres"
        title="Projects"
        subtitle="Tag POs, deliveries, production and fuel to a project to see real cost vs revenue."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `projects-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canManage && (
              <BtnPrimary onClick={openCreate} data-testid="project-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> New Project
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No projects yet." /></div> : (
          <table className="phizo-table">
            <thead>
              <tr><th>Code</th><th>Name</th><th>Customer</th><th>Status</th>
                <th className="text-right">Budget (USD)</th>
                <th>Period</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.code}`}>
                  <td className="mono font-semibold">{p.code}</td>
                  <td className="font-medium">{p.name}</td>
                  <td className="text-sm">{p.customer || "—"}</td>
                  <td><StatusBadge status={p.status === "on_hold" ? "draft" : p.status === "active" ? "approved" : p.status === "completed" ? "received" : p.status === "cancelled" ? "rejected" : "draft"} /></td>
                  <td className="text-right mono">{fmtUSD(p.budget_usd)}</td>
                  <td className="text-sm text-[#52525b]">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</td>
                  <td className="text-right whitespace-nowrap">
                    <BtnGhost onClick={() => openView(p)} data-testid={`project-view-${p.code}`}>
                      <Eye size={12} weight="bold" /> Cost view
                    </BtnGhost>
                    {canManage && (
                      <BtnGhost onClick={() => openEdit(p)}>
                        <PencilSimple size={12} weight="bold" />
                      </BtnGhost>
                    )}
                    {user.role === "admin" && (
                      <BtnGhost onClick={() => remove(p)} className="text-[#FF2A2A]">
                        <Trash size={12} weight="bold" />
                      </BtnGhost>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)}
        title={editing ? `Edit ${editing.code}` : "New Project"} wide>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <Input required disabled={!!editing} data-testid="proj-code"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="planning">planning</option>
              <option value="active">active</option>
              <option value="on_hold">on hold</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Name">
              <Input required data-testid="proj-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
          </div>
          <Field label="Customer">
            <SmartInput value={form.customer}
              onChange={(v) => {
                const match = customers.find((c) => c.name === v);
                setForm({ ...form, customer: v, customer_code: match ? match.code : "" });
              }}
              options={customers.map((c) => ({ value: c.name, hint: c.code }))} />
          </Field>
          <Field label="Budget (USD)">
            <Input type="number" step="0.01" value={form.budget_usd}
              onChange={(e) => setForm({ ...form, budget_usd: e.target.value })} />
          </Field>
          <Field label="Start Date">
            <Input type="date" value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </Field>
          <Field label="End Date">
            <Input type="date" value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </Field>
          <div className="col-span-2"><Field label="Description"><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
          <div className="col-span-2"><Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setEditOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="proj-save">Save</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* Cost view / P&L */}
      <Modal open={!!view} onClose={() => { setView(null); setSummary(null); setPnl(null); }}
        title={view ? `${view.code} — ${view.name}` : ""} wide testId="proj-view-modal">
        {view && (
          <div className="space-y-4">
            {/* Tabs */}
            <div className="flex border-b border-[#e4e4e7] -mx-6 px-6 -mt-2">
              {[
                { id: "cost", label: "Cost Centre" },
                { id: "pnl", label: "P&L (Revenue − Cost)" },
              ].map((t) => (
                <button
                  key={t.id} type="button"
                  data-testid={`proj-tab-${t.id}`}
                  onClick={() => {
                    setViewTab(t.id);
                    if (t.id === "pnl" && !pnl) loadPnl(view.id);
                  }}
                  className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${viewTab === t.id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#52525b] hover:text-[#09090b]"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {viewTab === "cost" && (
              summary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <MetricCard label="Budget" value={fmtUSD(summary.budget_usd)} accent="primary" />
                    <MetricCard label="PO Committed (ZMW)" value={fmtZMW(summary.po_committed_zmw)} delta={`${summary.po_count} POs`} deltaLabel="Count" />
                    <MetricCard label="Inventory Issued (ZMW)" value={fmtZMW(summary.inventory_issued_zmw)} delta={`${summary.inventory_movements} moves`} deltaLabel="Stock log" />
                    <MetricCard label="Fuel (ZMW)" value={fmtZMW(summary.fuel_cost_zmw)} delta={`${summary.fuel_litres} L`} deltaLabel="Diesel/Petrol" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="phizo-card p-5">
                      <div className="phizo-label mb-1">Production tagged to project</div>
                      <div className="font-heading text-2xl font-black mb-1">{summary.production_tonnes.toLocaleString()} t</div>
                      <div className="text-sm text-[#52525b]">Revenue: {fmtUSD(summary.production_revenue_usd)}</div>
                    </div>
                    <div className="phizo-card p-5">
                      <div className="phizo-label mb-1">Deliveries tagged to project</div>
                      <div className="font-heading text-2xl font-black mb-1">{summary.delivery_tonnes.toLocaleString()} t</div>
                      <div className="text-sm text-[#52525b]">{summary.delivery_count} delivery notes · {fmtUSD(summary.delivery_revenue_usd)}</div>
                    </div>
                  </div>
                  <div className="phizo-surface p-4">
                    <div className="phizo-label mb-2">Requests</div>
                    <div className="text-sm">{summary.request_count} requests · estimated cost: {fmtZMW(summary.request_estimated_zmw)}</div>
                  </div>
                  {summary.recent.purchase_orders.length > 0 && (
                    <div className="phizo-card">
                      <div className="p-4 border-b border-[#e4e4e7]"><div className="phizo-label">Recent POs</div></div>
                      <table className="phizo-table">
                        <thead><tr><th>PO</th><th>Supplier</th><th className="text-right">Total</th><th>Status</th></tr></thead>
                        <tbody>
                          {summary.recent.purchase_orders.map((po) => (
                            <tr key={po.id}>
                              <td className="mono">{po.number}</td>
                              <td>{po.supplier}</td>
                              <td className="text-right mono">{fmtZMW(po.total)}</td>
                              <td><StatusBadge status={po.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : <div className="p-6 text-sm text-[#52525b]">Loading cost view…</div>
            )}

            {viewTab === "pnl" && (
              pnl ? <PnlView pnl={pnl} /> : (
                <div className="p-6 text-sm text-[#52525b]" data-testid="pnl-loading">
                  Building P&amp;L…
                </div>
              )
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function PnlView({ pnl }) {
  const positive = pnl.net_zmw >= 0;
  return (
    <div className="space-y-4" data-testid="pnl-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Revenue (ZMW)" value={fmtZMW(pnl.revenue.revenue_zmw)}
          delta={`${pnl.revenue.tonnes.toLocaleString()} t · ${pnl.revenue.delivery_notes} DNs`} deltaLabel="Source"
          accent="success" />
        <MetricCard label="Total Cost (ZMW)" value={fmtZMW(pnl.cost.total_zmw)}
          delta="PO + Inventory + Fuel" deltaLabel="Stack" />
        <div className={`p-5 ${positive ? "bg-[#E6F7EE]" : "bg-[#FFF0F0]"} border ${positive ? "border-[#00A142]" : "border-[#FF2A2A]"}`}
             data-testid="pnl-net">
          <div className={`phizo-label ${positive ? "text-[#00A142]" : "text-[#FF2A2A]"}`}>Net (ZMW)</div>
          <div className="font-heading text-3xl font-black mt-1">
            {positive ? "+" : ""}{fmtZMW(pnl.net_zmw)}
          </div>
          <div className={`text-xs mt-1 mono ${positive ? "text-[#00A142]" : "text-[#FF2A2A]"}`}>
            Margin {pnl.margin_pct}%
          </div>
        </div>
        <MetricCard label="USD → ZMW Rate"
          value={(1 / pnl.usd_per_zmw).toFixed(2)}
          delta={`Rate $${pnl.usd_per_zmw.toFixed(4)}/ZMW`} deltaLabel="FX" />
      </div>

      <div className="phizo-card p-5">
        <div className="phizo-label mb-1">Monthly Revenue vs Cost</div>
        <div className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <ChartLineUp size={20} weight="bold" /> Profit &amp; Loss Trend
        </div>
        {pnl.series.length === 0 ? (
          <Empty label="No tagged transactions yet — add DNs and POs with this project code." />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={pnl.series}>
              <CartesianGrid stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11}
                tickFormatter={(v) => "K " + (v / 1000).toFixed(0) + "k"} />
              <Tooltip contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }}
                formatter={(v, n) => [fmtZMW(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#a1a1aa" />
              <Bar dataKey="revenue" name="Revenue (DNs)" stackId="rev" fill="#00A142" radius={0} />
              <Bar dataKey="po" name="Cost — POs" stackId="cost" fill="#002FA7" radius={0} />
              <Bar dataKey="inventory" name="Cost — Inventory" stackId="cost" fill="#7E22CE" radius={0} />
              <Bar dataKey="fuel" name="Cost — Fuel" stackId="cost" fill="#FFC000" radius={0} />
              <Line type="monotone" dataKey="net" name="Net" stroke="#09090b" strokeWidth={3} dot={{ fill: "#09090b", r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="phizo-card p-5">
          <div className="phizo-label mb-1">Cost Breakdown</div>
          <ul className="text-sm space-y-1.5 mt-3">
            <li className="flex justify-between"><span>Purchase Orders</span><span className="mono font-semibold">{fmtZMW(pnl.cost.purchase_orders_zmw)}</span></li>
            <li className="flex justify-between"><span>Inventory Issued</span><span className="mono font-semibold">{fmtZMW(pnl.cost.inventory_zmw)}</span></li>
            <li className="flex justify-between"><span>Fuel &amp; Diesel</span><span className="mono font-semibold">{fmtZMW(pnl.cost.fuel_zmw)}</span></li>
            <li className="flex justify-between border-t border-[#e4e4e7] pt-2 mt-2 font-bold">
              <span>Total</span><span className="mono">{fmtZMW(pnl.cost.total_zmw)}</span>
            </li>
          </ul>
        </div>
        <div className="phizo-card p-5">
          <div className="phizo-label mb-1">Revenue Composition</div>
          <ul className="text-sm space-y-1.5 mt-3">
            <li className="flex justify-between"><span>Delivery notes</span><span className="mono font-semibold">{pnl.revenue.delivery_notes}</span></li>
            <li className="flex justify-between"><span>Tonnes delivered</span><span className="mono font-semibold">{pnl.revenue.tonnes.toLocaleString()} t</span></li>
            <li className="flex justify-between"><span>Revenue (USD)</span><span className="mono font-semibold">{fmtUSD(pnl.revenue.revenue_usd)}</span></li>
            <li className="flex justify-between border-t border-[#e4e4e7] pt-2 mt-2 font-bold">
              <span>Revenue (ZMW)</span><span className="mono text-[#00A142]">{fmtZMW(pnl.revenue.revenue_zmw)}</span>
            </li>
          </ul>
        </div>
        <div className="phizo-card p-5">
          <div className="phizo-label mb-1">Budget vs Net</div>
          <ul className="text-sm space-y-1.5 mt-3">
            <li className="flex justify-between"><span>Budget (USD)</span><span className="mono font-semibold">{fmtUSD(pnl.project.budget_usd)}</span></li>
            <li className="flex justify-between"><span>Net (ZMW)</span><span className={`mono font-semibold ${positive ? "text-[#00A142]" : "text-[#FF2A2A]"}`}>
              {positive ? "+" : ""}{fmtZMW(pnl.net_zmw)}
            </span></li>
            <li className="flex justify-between border-t border-[#e4e4e7] pt-2 mt-2 font-bold">
              <span>Margin</span><span className={`mono ${positive ? "text-[#00A142]" : "text-[#FF2A2A]"}`}>{pnl.margin_pct}%</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
